import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
  type MutableRefObject,
} from "react";
import type { Annotation } from "../bits/AnnotationMenu";
import type { ContentsItem } from "../contexts/WithPublication/PublicationContext";
import { debounce, extractCSS, getHeadingParent, scopeCSS } from "../utils";

export function usePageStyles(styles: string | undefined, id: string) {
  useEffect(() => {
    if (!styles) return;
    const cleanedCss = extractCSS(styles);
    const scopedCSS = scopeCSS(cleanedCss, `#${id}`);
    const style = document.createElement("style");
    style.textContent = scopedCSS;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, [styles, id]);
}

export function useResizeScrollPreservation(
  currentChunk: string | undefined,
  currentSection: ContentsItem | undefined,
): {
  currentChunkRef: MutableRefObject<HTMLDivElement | null>;
  resizeTimerRef: MutableRefObject<NodeJS.Timeout | null>;
} {
  const resizeTimerRef = useRef<NodeJS.Timeout>(null);
  const targetElementRef = useRef<Element>(null);
  const currentChunkRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const getDeepestTopElement = (
      parentElement: Element | null,
    ): Element | null => {
      if (!parentElement) return null;
      if (parentElement.children.length === 0) return parentElement;
      for (const child of parentElement.children) {
        const rect = child.getBoundingClientRect();
        if (rect.bottom > 60) return getDeepestTopElement(child);
      }
      return parentElement;
    };

    const handleResize = () => {
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
      resizeTimerRef.current = setTimeout(() => {
        if (targetElementRef.current) {
          targetElementRef.current.scrollIntoView({
            behavior: "auto",
            block: "start",
          });
        }
        resizeTimerRef.current = null;
        targetElementRef.current = getDeepestTopElement(
          currentChunkRef.current,
        );
      }, 300);
    };

    targetElementRef.current = getDeepestTopElement(currentChunkRef.current);
    window.addEventListener("resize", handleResize);
    screen.orientation?.addEventListener("change", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      screen.orientation?.removeEventListener("change", handleResize);
    };
  }, [currentChunk, currentSection]);

  return { currentChunkRef, resizeTimerRef };
}

export function useHighlightMenu({
  clearHighlight,
  containerRef,
  htmlChunks,
  currentSection,
}: {
  clearHighlight: (key?: string) => void;
  containerRef: RefObject<HTMLDivElement | null>;
  htmlChunks: Record<number, string> | undefined;
  currentSection: ContentsItem | undefined;
}) {
  const [annotationSaved, setAnnotationSaved] = useState(false);
  const [currentHighlightMenuOpen, setCurrentHighlightMenuOpen] =
    useState(false);
  const [annotationPosition, setAnnotationPosition] = useState<DOMRect>();
  const [currentAnnotation, setCurrentAnnotation] = useState<Annotation>();

  const closeCurrentHighlightMenu = useCallback(() => {
    setCurrentHighlightMenuOpen(false);
    setAnnotationPosition(undefined);
    setCurrentAnnotation(undefined);
    setAnnotationSaved(false);
    clearHighlight("selected");
  }, [clearHighlight]);

  const handleResize = useCallback(() => {
    closeCurrentHighlightMenu();
    window.getSelection()?.removeAllRanges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeCurrentHighlightMenu, currentSection]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      handleResize();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [containerRef, handleResize, htmlChunks]);

  return {
    annotationSaved,
    setAnnotationSaved,
    currentHighlightMenuOpen,
    setCurrentHighlightMenuOpen,
    annotationPosition,
    setAnnotationPosition,
    currentAnnotation,
    setCurrentAnnotation,
    closeCurrentHighlightMenu,
  };
}

export function useScrollSectionTracker({
  id,
  slug,
  contents,
  currentSection,
  selectSection,
  scrollDisabled,
  resizeTimerRef,
  closeCurrentHighlightMenu,
}: {
  id: string;
  slug: string | undefined;
  contents: ContentsItem[] | undefined;
  currentSection: ContentsItem | undefined;
  selectSection: (
    id: string,
    section?: ContentsItem,
    noScroll?: boolean,
  ) => void;
  scrollDisabled: boolean | undefined;
  resizeTimerRef: { current: NodeJS.Timeout | null };
  closeCurrentHighlightMenu: () => void;
}) {
  useEffect(() => {
    const container = document.getElementById(id);
    const scrollContainer =
      document.getElementById("ereader-content") ||
      document.scrollingElement ||
      document;

    if (!container) return;

    const elements = Array.from(
      container.querySelectorAll<HTMLElement>("[id]"),
    );

    const handleScroll = () => {
      closeCurrentHighlightMenu();
      window.getSelection()?.removeAllRanges();
      if (scrollDisabled || resizeTimerRef.current) return;
      const closest = elements
        .filter((el) => el.id.startsWith(`${slug}-`))
        .map((el) => {
          const rect = el.getBoundingClientRect();
          return { id: el.id, top: rect.top };
        })
        .filter((el) => el.top <= 60)
        .sort((a, b) => b.top - a.top)[0];

      if (closest && currentSection?.id !== closest.id) {
        const section = getHeadingParent(closest.id, contents || []);
        if (section && section.id !== currentSection?.id) {
          selectSection(section.id, section, true);
        }
      }
    };

    const debouncedScroll = debounce(handleScroll, 100);

    scrollContainer.addEventListener("scroll", debouncedScroll, {
      passive: true,
    });
    screen.orientation?.addEventListener("change", debouncedScroll);
    return () => {
      scrollContainer.removeEventListener("scroll", debouncedScroll);
      screen.orientation?.removeEventListener("change", debouncedScroll);
    };
  }, [
    id,
    slug,
    contents,
    currentSection,
    selectSection,
    scrollDisabled,
    closeCurrentHighlightMenu,
    resizeTimerRef,
  ]);
}
